import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { black1, grayDefault, grayText, grey10, white, yellow } from 'app/components/ThemeProvider/colors';
import TableDotsIcon from 'app/components/Icons/TableDotsIcon';
import React, { useEffect, useState } from 'react';
import {
  lastOperationsNone,
  menuItems,
  searchNone,
  tableCell,
  tableCellStatus,
  tableRowStyle,
} from './LastOperations.sx';
import DbIcon from 'app/components/Icons/DbIcon';
import CrIcon from 'app/components/Icons/CrIcon';
import { LastOperationsType } from 'app/constants/types';
import SearchIcon from 'app/components/Icons/SearchIcon';
import useLastOperationsTable from 'app/components/LastOperations/useLastOperationsTable';
import DetailsModal from 'app/components/LastOperations/DetailsModal';
import AsanConfirmation from 'app/components/AsanConfirmation';
import LoaderFullScreen from 'app/components/LoaderFullScreen';
import logger from 'app/utils/logger';

interface Props {
  tableRow: LastOperationsType[];
  bg?: boolean;
  loader?: boolean;
  handleMoveTransfer?: any;
  menuButtons?: any;
}

const LastOperationsTable = (props: Props) => {
  const { tableRow, bg, loader, handleMoveTransfer,menuButtons } = props;

  const {
    anchorEl,
    handleMenuClick,
    handleClose,
    activeAnchor,
    mediumScreens,
    details,
    handleClickMenu,
    openDetailsModal,
    setOpenDetailsModal,
    dataGetTransferDetails,
    asanConfirmation,
    setAsanConfirmation,
    status,
    isSuccessGetTransferDetails,
    isLoadingGetTransferDetails,
    signType,
    type,
  } = useLastOperationsTable({menuButtons});


  if (loader) {
    return (
        <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              height: '543px',
            }}
        >
          <CircularProgress />
        </Box>
    );
  }

  return (
      <>
        {tableRow?.length !== 0 ? (
            <TableContainer sx={{ display: 'flex', borderRadius: '8px' }}>
              <Table>
                <TableBody>
                  {tableRow?.map((row, index) => (
                      <TableRow
                          key={index}
                          sx={
                            !bg
                                ? (index + 1) % 2 !== 0
                                    ? {
                                      ...tableRowStyle,
                                      background: grey10,
                                    }
                                    : { ...tableRowStyle, background: white }
                                : (index + 1) % 2 !== 0
                                    ? { ...tableRowStyle, background: white }
                                    : { ...tableRowStyle, background: grey10 }
                          }
                      >
                        <TableCell sx={{ ...tableCell, width: '40px' }}>
                          {row.db_cr === 'cr' ? <DbIcon /> : <CrIcon />}
                        </TableCell>
                        <TableCell sx={{ ...tableCell, width: '455px' }}>
                          <Typography
                              sx={{
                                fontSize: '17px',
                                color: black1,
                                lineHeight: '1',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                fontWeight: 600
                              }}
                          >
                            {row.counter_name}
                          </Typography>
                          <Typography
                              sx={{
                                fontSize: '15px',
                                color: 'rgba(20, 25, 31, 0.56)',
                                lineHeight: '1',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                              }}
                          >
                            {row.trn_description}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...tableCell, width: '120px' }}>
                          <Typography sx={{ fontSize: '17px', color: black1, lineHeight: '1',fontWeight: 600 }}>
                            {row.trn_amount} {row.ccy_symbol}
                          </Typography>
                          <Typography
                              sx={{
                                fontSize: '15px',
                                color: 'rgba(20, 25, 31, 0.56)',
                                lineHeight: '1',
                              }}
                          >
                            {row.trn_date}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...tableCellStatus, width: '155px', display: 'flex', justifyContent: 'flex-start' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px',justifyContent: 'flex-start' }}>
                            <Box
                                sx={{ width: '12px', height: '12px', borderRadius: '50%', background: `${row.status_color}` }}
                            ></Box>
                            <Typography>{row.status_name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ p: '21px 0', border: 'none', display: 'flex', alignItems: 'center' }}>
                          <Box
                              sx={{
                                border: '1px solid rgba(20, 25, 31, 0.2)',
                                borderRadius: '8px',
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': { cursor: 'pointer' },
                              }}
                              onClick={(event: any) => handleMenuClick(event, row.trn_id, row.state, row.db_cr,row.menu_buttons)}
                          >
                            <TableDotsIcon
                                sx={{ '&:hover': { cursor: 'pointer' } }}
                                aria-controls={anchorEl ? `actions-${row.trn_id}` : undefined}
                                aria-haspopup="true"
                                aria-expanded={anchorEl ? 'true' : undefined}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Menu
                  id={activeAnchor}
                  disableAutoFocusItem
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  sx={{ '& .MuiPaper-root': { right: '30px', left: 'auto !important' } }}
              >
                {Object.values(details).map(detail => (
                        <MenuItem key={detail.action} sx={menuItems} onClick={() => handleClickMenu(detail.action)}>
                          <img style={{ width: 18 }} src={detail.icon} alt="detail icon" />
                          <Typography sx={{ color: detail.action === 'sign' ? '#24B07E' : null }}>
                            {detail.text}
                          </Typography>
                        </MenuItem>
                ))}
              </Menu>
              {isSuccessGetTransferDetails && dataGetTransferDetails?.print_forms !== undefined
                  ? dataGetTransferDetails?.print_forms.map((item: any, index: any) => (
                      <a
                          href={`${item.fileLink}`}
                          target="_blank"
                          rel="noreferrer"
                          key={index}
                          className="filesLastOperations"
                      ></a>
                  ))
                  : null}

              {isLoadingGetTransferDetails && <LoaderFullScreen open={isLoadingGetTransferDetails} />}
            </TableContainer>
        ) : (
            <Box sx={lastOperationsNone}>
              <Box sx={searchNone}>
                <SearchIcon />
              </Box>
              <Typography sx={{ fontSize: '16px', color: grayText }}>
                Sizin hal-hazırda əks olunacaq əməliyyatınız yoxdur
              </Typography>
              <Button size={mediumScreens ? 'medium' : 'large'} onClick={handleMoveTransfer} sx={{ mt: '12px' }}>
                Köçürmə yarat
              </Button>
            </Box>
        )}
        {openDetailsModal && (
            <DetailsModal open={openDetailsModal} close={setOpenDetailsModal} dataDetails={dataGetTransferDetails} />
        )}
        <AsanConfirmation
            open={asanConfirmation}
            close={setAsanConfirmation}
            verifyCode={dataGetTransferDetails?.verification_code}
            timerTime={dataGetTransferDetails?.timer}
            pathUrl={dataGetTransferDetails?.path_url}
            businessKey={dataGetTransferDetails?.business_key}
        />
      </>
  );
};

export default LastOperationsTable;
